const APPLE_JS_SDK =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

let scriptLoadPromise;

export function isAppleSignInConfigured() {
  return Boolean(String(process.env.REACT_APP_APPLE_CLIENT_ID || "").trim());
}

function appleClientId() {
  return String(process.env.REACT_APP_APPLE_CLIENT_ID || "").trim();
}

function appleRedirectUri() {
  const fromEnv = String(process.env.REACT_APP_APPLE_REDIRECT_URI || "").trim();
  if (fromEnv) return fromEnv;
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/login`;
}

function randomNonce() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function loadAppleScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Apple sign-in is only available in the browser."));
  }
  if (window.AppleID?.auth) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${APPLE_JS_SDK}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Apple sign-in.")));
      if (window.AppleID?.auth) resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = APPLE_JS_SDK;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("Failed to load Apple sign-in."));
    };
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

function isUserCancel(error) {
  const code = error?.error || error?.code || "";
  const message = error?.message || String(error || "");
  return /popup_closed_by_user|user_cancelled|USER_CANCELLED|popup_closed/i.test(
    `${code} ${message}`
  );
}

/**
 * Popup Sign in with Apple. Returns `{ idToken, nonce }` or `null` if the user cancels.
 */
export async function signInWithApple() {
  const clientId = appleClientId();
  if (!clientId) {
    const err = new Error("Apple sign-in is not configured.");
    err.code = "not_configured";
    throw err;
  }
  await loadAppleScript();
  if (!window.AppleID?.auth) {
    throw new Error("Apple sign-in is unavailable.");
  }
  const nonce = randomNonce();
  const redirectURI = appleRedirectUri();
  window.AppleID.auth.init({
    clientId,
    scope: "name email",
    redirectURI,
    usePopup: true,
    nonce,
    state: nonce,
  });
  try {
    const response = await window.AppleID.auth.signIn();
    console.log("[Apple signIn] response", response);
    const idToken = response?.authorization?.id_token;
    if (!idToken) {
      throw new Error("Apple did not return an identity token.");
    }
    return {
      idToken,
      nonce,
      code: response?.authorization?.code || "",
      user: response?.user || null,
      raw: response,
    };
  } catch (error) {
    if (isUserCancel(error)) return null;
    const message =
      (typeof error?.error === "string" && error.error) ||
      error?.message ||
      "Apple sign-in failed.";
    const wrapped = new Error(message);
    wrapped.cause = error;
    throw wrapped;
  }
}
